import {
  InfrastructureType,
  InfrastructureLevel,
  InfrastructureRelationship,
  infrastructureType,
  infrastructureLevel,
  infrastructureRelationship,
  infrastructureTypes,
  infrastructureLevels,
  infrastructureRelationships,
} from '@nohara-dxdev/mt-common';

/**
 * Mapping from human-readable infrastructure type names (Japanese and English) to InfrastructureType enum values
 */
const infrastructureTypeMapping: Record<string, InfrastructureType> = {
  // Japanese names
  電気: infrastructureType.electricity,
  通信: infrastructureType.telecommunications,
  給水: infrastructureType.water_supply,
  温水: infrastructureType.hot_water,
  冷水: infrastructureType.cold_water,
  冷却水: infrastructureType.cooling_water,
  排水: infrastructureType.drainage,
  ガス: infrastructureType.gas,
  地域熱源: infrastructureType.dhc,
  空気: infrastructureType.air,

  // English names
  Electricity: infrastructureType.electricity,
  Telecommunications: infrastructureType.telecommunications,
  'Water Supply': infrastructureType.water_supply,
  'Water supply': infrastructureType.water_supply,
  'Hot Water': infrastructureType.hot_water,
  'Hot water': infrastructureType.hot_water,
  'Cold Water': infrastructureType.cold_water,
  'Cold water': infrastructureType.cold_water,
  'Cooling Water': infrastructureType.cooling_water,
  'Cooling water': infrastructureType.cooling_water,
  Drainage: infrastructureType.drainage,
  Gas: infrastructureType.gas,
  DHC: infrastructureType.dhc,
  Air: infrastructureType.air,

  // Case-insensitive variations (lowercase)
  electricity: infrastructureType.electricity,
  telecommunications: infrastructureType.telecommunications,
  'water supply': infrastructureType.water_supply,
  water_supply: infrastructureType.water_supply,
  'hot water': infrastructureType.hot_water,
  hot_water: infrastructureType.hot_water,
  'cold water': infrastructureType.cold_water,
  cold_water: infrastructureType.cold_water,
  'cooling water': infrastructureType.cooling_water,
  cooling_water: infrastructureType.cooling_water,
  drainage: infrastructureType.drainage,
  gas: infrastructureType.gas,
  dhc: infrastructureType.dhc,
  air: infrastructureType.air,
};

/**
 * Mapping from human-readable relationship level names (Japanese and English) to InfrastructureLevel enum values
 */
const infrastructureLevelMapping: Record<string, InfrastructureLevel> = {
  // Japanese names
  主: infrastructureLevel.main,
  副: infrastructureLevel.sub,

  // English names
  Main: infrastructureLevel.main,
  Sub: infrastructureLevel.sub,

  // Case-insensitive variations (lowercase)
  main: infrastructureLevel.main,
  sub: infrastructureLevel.sub,
};

/**
 * Mapping from human-readable relationship names (Japanese and English) to InfrastructureRelationship enum values
 */
const infrastructureRelationshipMapping: Record<string, InfrastructureRelationship> = {
  // Japanese names
  親: infrastructureRelationship.parent,
  子: infrastructureRelationship.child,
  親子: infrastructureRelationship.both,

  // English names
  Parent: infrastructureRelationship.parent,
  Child: infrastructureRelationship.child,
  Both: 'both',

  // Case-insensitive variations (lowercase)
  parent: infrastructureRelationship.parent,
  child: infrastructureRelationship.child,
  both: 'both',
};

/**
 * Map infrastructure type name (Japanese or English) to InfrastructureType enum value
 * Supports:
 * - Japanese names from translation files
 * - English human-readable names
 * - Enum values directly
 * - Case-insensitive matching
 */
export const mapInfrastructureType = (typeName: string): InfrastructureType | null => {
  const trimmedName = typeName.trim();

  // Try exact match first (case-sensitive, for Japanese)
  if (trimmedName in infrastructureTypeMapping) {
    return infrastructureTypeMapping[trimmedName];
  }

  // Try case-insensitive match (for English variations)
  const lowerCaseName = trimmedName.toLowerCase();
  if (lowerCaseName in infrastructureTypeMapping) {
    return infrastructureTypeMapping[lowerCaseName];
  }

  // Check if it's already a valid enum value
  if (infrastructureTypes.includes(trimmedName as InfrastructureType)) {
    return trimmedName as InfrastructureType;
  }

  return null;
};

/**
 * Map relationship level name (Japanese or English) to InfrastructureLevel enum value
 * Supports:
 * - Japanese names from translation files
 * - English human-readable names
 * - Enum values directly
 * - Case-insensitive matching
 */
export const mapInfrastructureLevel = (levelName: string): InfrastructureLevel | null => {
  const trimmedName = levelName.trim();

  // Try exact match first (case-sensitive, for Japanese)
  if (trimmedName in infrastructureLevelMapping) {
    return infrastructureLevelMapping[trimmedName];
  }

  // Try case-insensitive match (for English variations)
  const lowerCaseName = trimmedName.toLowerCase();
  if (lowerCaseName in infrastructureLevelMapping) {
    return infrastructureLevelMapping[lowerCaseName];
  }

  // Check if it's already a valid enum value
  if (infrastructureLevels.includes(trimmedName as InfrastructureLevel)) {
    return trimmedName as InfrastructureLevel;
  }

  return null;
};

/**
 * Map infrastructure relationship name (Japanese or English) to InfrastructureRelationship enum value
 * Supports:
 * - Japanese names from translation files
 * - English human-readable names
 * - Enum values directly
 * - Case-insensitive matching
 * - Special "both" value (parent OR child)
 */
export const mapInfrastructureRelationship = (
  relationshipName: string,
): InfrastructureRelationship | 'both' | null => {
  const trimmedName = relationshipName.trim();

  // Try exact match first (case-sensitive, for Japanese)
  if (trimmedName in infrastructureRelationshipMapping) {
    return infrastructureRelationshipMapping[trimmedName];
  }

  // Try case-insensitive match (for English variations)
  const lowerCaseName = trimmedName.toLowerCase();
  if (lowerCaseName in infrastructureRelationshipMapping) {
    return infrastructureRelationshipMapping[lowerCaseName];
  }

  // Check if it's already a valid enum value
  if (infrastructureRelationships.includes(trimmedName as InfrastructureRelationship)) {
    return trimmedName as InfrastructureRelationship;
  }

  if (trimmedName.toLowerCase() === 'both') {
    return 'both';
  }

  return null;
};

/**
 * Get all supported infrastructure type name variations for display/documentation
 */
export const getSupportedInfrastructureTypeNames = (): Record<InfrastructureType, string[]> => {
  const result: Record<string, string[]> = {};

  // Group all names by their target type
  for (const [name, type] of Object.entries(infrastructureTypeMapping)) {
    if (!(type in result)) {
      result[type] = [];
    }
    result[type].push(name);
  }

  return result as Record<InfrastructureType, string[]>;
};
